/**
 * "Phone or email, each valid if present" — the identity rule for managed
 * users. Pure, so both add dialogs share one implementation and the rule is
 * unit-tested without a form. Mirrors functions/_helpers/managedIdentity.js.
 */
export const PHONE_RE = /^05\d{8}$/;
export const EMAIL_RE = /^\S+@\S+\.\S+$/;

export type IdentityInput = { phone?: string; email?: string };
export type IdentityResult =
  | { ok: true; phone: string | null; email: string | null; identity: 'phone' | 'email' | 'both' }
  | { ok: false; field: 'phone' | 'email'; code: 'required' | 'phone-format' | 'email-format' };

/** '05XXXXXXXX' from any spacing / +966 form, or null. */
export function normalizeLocalPhone(raw: string | undefined): string | null {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return null;
  const local = digits.startsWith('966') ? '0' + digits.slice(3) : digits;
  return PHONE_RE.test(local) ? local : null;
}

export function validateIdentity(input: IdentityInput): IdentityResult {
  const phoneGiven = String(input.phone || '').trim() !== '';
  const emailGiven = String(input.email || '').trim() !== '';
  if (!phoneGiven && !emailGiven) return { ok: false, field: 'phone', code: 'required' };

  const phone = phoneGiven ? normalizeLocalPhone(input.phone) : null;
  if (phoneGiven && !phone) return { ok: false, field: 'phone', code: 'phone-format' };

  const email = emailGiven ? String(input.email).trim().toLowerCase() : null;
  if (emailGiven && !EMAIL_RE.test(email as string)) return { ok: false, field: 'email', code: 'email-format' };

  return { ok: true, phone, email, identity: phone && email ? 'both' : phone ? 'phone' : 'email' };
}
